import { EmailTemplatesFormState } from '../utils/emailTemplateFormState';
import { MoneyflowOfferOverviewResult } from '../../../../../../types/internal-api';

export interface BookAndSendFormData extends EmailTemplatesFormState {
    sendByType: SendByType;
    isCreditNote: boolean;
    moneyflowOffer: MoneyflowOfferOverviewResult;
    moneyflowEinvoiceEmails: string[];
    /**
     * Index in moneyflowEinvoiceEmails array
     *
     * @type {number}
     * @memberof BookAndSendFormData
     */
    moneyflowEinvoiceEmail: number;
    canSendElectronically: boolean;
    canIncludePaymentLink: boolean;
    canToggleMoneyflow: boolean;
    includePaymentLink: boolean;
    messages: InvoicingMessage[];
    skipBookOnlyConfirmation: boolean;
    emailOptions: {
        subject: string;
        body: string;
        encrypt: boolean;
        showPdf: boolean;
        includePdfLink: boolean;
        includePdfAsAttachment: boolean;
        includeOioLink: boolean;
        includeOioAsAttachment: boolean;
        recipients: string;
        recipientsCc: string;
        recipientsBcc: string;
        saveSubject: boolean;
        saveBody: boolean;
    };
}

export interface SendEmailOptions {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    showPdf: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
    saveSubject: boolean;
    saveBody: boolean;
}

export interface EmailOptionsRequest {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

export interface SendEmailOptionsRequest extends EmailOptionsRequest {
    saveSubject: boolean;
    saveBody: boolean;
}

export interface InvoiceSendEmailOptionsRequest {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    saveSubject: boolean;
    saveBody: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
    encrypt: boolean;
}

export interface InvoicingMessage {
    type: InvoicingMessageType;
    message: string;
}

export interface GetInformationInvoiceResult {
    sendByType: SendByType;
    isCreditNote: boolean;
    moneyflowOffer: MoneyflowOfferOverviewResult;
    canSendElectronically: boolean;
    canIncludePaymentLink: boolean;
    canToggleMoneyflow: boolean;
    emailOptions: SendEmailOptions;
    messages: InvoicingMessage[];
}

export interface GetInformationBookedInvoiceResult {
    isCreditNote: boolean;
    canSendElectronically: boolean;
    EmailOptions: SendEmailOptions;
}

export interface InvoiceBookAndSendRequest {
    sendByType: SendByType;
    invoiceDraftId: number;
    showPdf: boolean;
    includePaymentLink: boolean;
    emailOptions: InvoiceSendEmailOptionsRequest;
}

export interface InvoiceBookAndSendResponse {
    documentId: number;
    pdfLink: string;
    messages: InvoicingMessage[];
}

export enum InvoicingMessageType {
    Info = 0,
    Warning = 1,
    Error = 2,
}

export enum SendByType {
    undefined = 0,
    oioxml = 1,
    email = 2,
}

export interface OrderSendRequest {
    sendByType: SendByType;
    orderId: number;
    showPdf: boolean;
    emailOptions: SendEmailOptionsRequest;
}

export interface OrderSendResult {
    pdfLink: string;
}

export interface DraftInvoiceErrorCollection {
    invoiceDraftId: number;
    messages: string[];
    displayNumber: number;
}

export interface SendElectronicInvoiceInfo {
    draftId: number;
    canSend: boolean;
}

export enum SendElectronicInvoiceMethod {
    GlnNumber = 0,
    CvrNumber = 1,
    PNumber = 2,
}

export interface BulkInvoicesBookingInfoResult {
    subject: string;
    body: string;
    messages: InvoicingMessage[];
    draftInvoiceErrors: DraftInvoiceErrorCollection[];
    checkedShowPdf: boolean;
    checkedEncrypt: boolean;
    checkedEmailLinkPdf: boolean;
    checkedEmailLinkOIOXML: boolean;
    checkedAttachPdf: boolean;
    checkedAttachOIOXML: boolean;
    canIncludePaymentLink: boolean;
    newInvoiceDate: string;
    sendElectronicInvoicesInfo: SendElectronicInvoiceInfo[];
}

export interface BulkBookAndSendEmailOptions {
    subject?: string;
    body?: string;
    includeOioAsAttachment: boolean;
    includePdfAsAttachment: boolean;
    encrypt: boolean;
    includeOioLink: boolean;
    includePdfLink: boolean;
    saveSubject?: boolean;
    saveBody?: boolean;
}

export interface BulkBookAndSendFormData extends EmailTemplatesFormState {
    bookOnly: boolean;
    changeInvoiceDate: boolean;
    newInvoiceDate: string | null;
    showInvoicesAfterBooking: boolean;
    includePaymentLink: boolean;
    emailOptions: BulkBookAndSendEmailOptions;
    invoicesToSendElectronically: number[];
    invoicesToSendByEmail: number[];
    skipBookOnlyConfirmation: boolean;
}

export interface BulkBookAndSendViewData {
    sendElectronicallyCount: number;
    sendByEmailCount: number;
    skippedCount: number;
    nonBlockingWarnings: InvoicingMessage[];
    blockingErrors: InvoicingMessage[];
    skipBookOnlyConfirmation: boolean;
    canIncludePaymentLink: boolean;
}

export interface BulkBookAndSendData {
    formData: BulkBookAndSendFormData;
    viewData: BulkBookAndSendViewData;
}

export interface BookBulkInvoicesRequest {
    bookOnly: boolean;
    newInvoiceDate: string | null;
    showInvoicesAfterBooking: boolean;
    includePaymentLink: boolean;
    emailOptions: BulkBookAndSendEmailOptions;
    invoicesToSendElectronically: number[];
    invoicesToSendByEmail: number[];
}

export interface BookBulkInvoicesResponse {
    pdfLink: string;
    failedToBookCount: number;
    failedToSendCount: number;
    bookedInvoices: number[];
    messages: InvoicingMessage[];
}
